// ManageFlows.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import BackButton from "../components/BackButton"; // ✅ เพิ่มปุ่มย้อนกลับ

const ManageFlows = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const response = await axios.get(`${apiURL}approve-flows`);
        setFlows(response.data.flows || []);
      } catch (error) {
        console.error("Error fetching flows:", error);
        toast.error("เกิดข้อผิดพลาดในการดึงข้อมูล Flows", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchFlows();
  }, []);

  const handleDelete = async (flowId) => {
    if (window.confirm("คุณต้องการลบ Flow นี้หรือไม่?")) {
      try {
        await axios.delete(`${apiURL}approve-flows/delete/${flowId}`);
        setFlows(flows.filter((flow) => flow._id !== flowId));
        toast.success("ลบ Flow สำเร็จ!", {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error("Error deleting flow:", error);
        toast.error("เกิดข้อผิดพลาดในการลบ Flow", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  const handleView = (flow) => {
    setSelectedFlow(flow);
  };

  const closeModal = () => {
    setSelectedFlow(null);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-50">
        <ClipLoader color="#6366F1" loading={loading} size={80} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 md:px-6 py-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header Section */}
      <div className="max-w-6xl mx-auto flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-200">
          Manage Approve Flows
        </h1>
        <BackButton label="ย้อนกลับ" />
      </div>

      {/* Table Section */}
      <div className="max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6">
        {flows.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300 text-center py-6">
            ยังไม่มี Flow ที่สร้าง
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-left">
              <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <tr>
                  <th className="px-3 md:px-6 py-3 font-semibold">#</th>
                  <th className="px-3 md:px-6 py-3 font-semibold">Flow Name</th>
                  <th className="px-3 md:px-6 py-3 font-semibold text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {flows.map((flow, index) => (
                  <tr
                    key={flow._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                  >
                    <td className="px-3 md:px-6 py-3 text-gray-700 dark:text-gray-300">
                      {index + 1}
                    </td>
                    <td className="px-3 md:px-6 py-3 text-gray-700 dark:text-gray-200">
                      {flow.name}
                    </td>
                    <td className="px-3 md:px-6 py-3 text-center">
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          className="px-3 py-1 text-xs rounded-md bg-indigo-500 hover:bg-indigo-600 text-white transition-all"
                          onClick={() => handleView(flow)}
                        >
                          View
                        </button>
                        <button
                          className="px-3 py-1 text-xs rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-all"
                          onClick={() => navigate(`/edit-flow/${flow._id}`)}
                        >
                          Update
                        </button>
                        <button
                          className="px-3 py-1 text-xs rounded-md bg-red-500 hover:bg-red-600 text-white transition-all"
                          onClick={() => handleDelete(flow._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Flow Details */}
      {selectedFlow && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm px-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Flow Details
            </h2>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              <strong>Name:</strong> {selectedFlow.name}
            </p>

            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-4">
              Approval Hierarchy:
            </h3>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
              {selectedFlow.approvalHierarchy.map((level) => (
                <li key={level.level}>
                  <strong>Level {level.level}:</strong> {level.approver}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-end">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-md transition-all"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ManageFlows;
